import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createPaginatedRowModel,
	useTable,
} from '@tanstack/react-table';
import dayjs from 'dayjs';

import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createFilterOnlyColumn } from '@apple/ui/data-table/utils/filters';
import { TableLayout } from '@apple/ui/layouts/TableLayout';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import {
	type Queue,
	type QueueFilters,
	queueFiltersSchema,
} from '@apple/features/queue-message/models';
import { queueMessageQueries } from '@apple/features/queue-message/queries';

const features = createDataTableFeatures({
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

const defaultFilters: QueueFilters = {
	lastAttemptOn: dayjs().subtract(2, 'days').startOf('day').format('YYYY-MM-DD'),
};

export const Route = createFileRoute('/_authed/_admin/manage/queue-message')({
	validateSearch: queueFiltersSchema,

	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductManagement.Read',
		});
	},

	component: QueueMessageManagementRoute,
});

function QueueMessageManagementRoute() {
	const { t } = useTranslation('manage');
	const navigate = Route.useNavigate();
	const search = Route.useSearch();
	const tableState = useTableState<Queue, QueueFilters>({
		search,
		navigate,
		defaultFilters,
		fieldMap: [],
	});

	const queuesQuery = useQuery(queueMessageQueries.getQueues(tableState.currentFilterData));

	const table = useTable({
		features,
		data: queuesQuery.data ?? [],
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		manualFiltering: true,
		enableSorting: false,
		columnResizeMode: 'onChange',

		columns: [
			{
				accessorKey: 'queue',
				header: 'Queue',
				size: 300,
				filter: {
					group: t('queues.filterGroups.details'),
				},
			},
			createFilterOnlyColumn<Queue, QueueFilters>({
				field: 'lastAttemptOn',
				label: t('queues.filters.lastAttemptOn'),
				variant: 'date',
				group: t('queues.filterGroups.details'),
			}),
			createFilterOnlyColumn<Queue, QueueFilters>({
				field: 'completedOn',
				label: t('queues.filters.completedOn'),
				variant: 'date',
				group: t('queues.filterGroups.details'),
			}),
			{
				accessorKey: 'queuedCount',
				header: 'Queued',
				enableColumnFilter: false,
				size: 200,
				cell: ({ row }) => {
					const count = row.original.queuedCount;

					return count > 0 ? (
						<Link
							to='/manage/queue-message/$queue/$status'
							params={{
								queue: row.original.queue,
								status: 'Queued',
							}}
							search={{
								lastAttemptOn: tableState.currentFilterData.lastAttemptOn,
								completedOn: tableState.currentFilterData.completedOn,
							}}
						>
							{count}
						</Link>
					) : (
						count
					);
				},
			},
			{
				accessorKey: 'scheduledCount',
				header: 'Scheduled',
				enableColumnFilter: false,
				size: 200,
				cell: ({ row }) => {
					const count = row.original.scheduledCount;

					return count > 0 ? (
						<Link
							to='/manage/queue-message/$queue/$status'
							params={{
								queue: row.original.queue,
								status: 'Scheduled',
							}}
							search={{
								lastAttemptOn: tableState.currentFilterData.lastAttemptOn,
								completedOn: tableState.currentFilterData.completedOn,
							}}
						>
							{count}
						</Link>
					) : (
						count
					);
				},
			},
			{
				accessorKey: 'failedCount',
				header: 'Failed',
				enableColumnFilter: false,
				size: 200,
				cell: ({ row }) => {
					const count = row.original.failedCount;

					return count > 0 ? (
						<Link
							to='/manage/queue-message/$queue/$status'
							params={{
								queue: row.original.queue,
								status: 'Failed',
							}}
							search={{
								lastAttemptOn: tableState.currentFilterData.lastAttemptOn,
								completedOn: tableState.currentFilterData.completedOn,
							}}
						>
							{count}
						</Link>
					) : (
						count
					);
				},
			},
			{
				accessorKey: 'abandonedCount',
				header: 'Abandoned',
				enableColumnFilter: false,
				size: 200,
				cell: ({ row }) => {
					const count = row.original.abandonedCount;

					return count > 0 ? (
						<Link
							to='/manage/queue-message/$queue/$status'
							params={{
								queue: row.original.queue,
								status: 'Abandoned',
							}}
							search={{
								lastAttemptOn: tableState.currentFilterData.lastAttemptOn,
								completedOn: tableState.currentFilterData.completedOn,
							}}
						>
							{count}
						</Link>
					) : (
						count
					);
				},
			},
			{
				accessorKey: 'completedCount',
				header: 'Completed',
				enableColumnFilter: false,
				size: 200,
				cell: ({ row }) => {
					const count = row.original.completedCount;

					return count > 0 ? (
						<Link
							to='/manage/queue-message/$queue/$status'
							params={{
								queue: row.original.queue,
								status: 'Completed',
							}}
							search={{
								lastAttemptOn: tableState.currentFilterData.lastAttemptOn,
								completedOn: tableState.currentFilterData.completedOn,
							}}
						>
							{count}
						</Link>
					) : (
						count
					);
				},
			},
		],
	});

	return (
		<TableLayout title={t('queues.title')} table={table}>
			<DataTable table={table} loading={queuesQuery.isFetching} />
		</TableLayout>
	);
}
